import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import SimpleBar from 'simplebar-react';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import DatePickerCustom from 'src/components/common/field/DatePickerCustom';
import DropDown from 'src/components/common/field/DropDown';
import InputCustom from 'src/components/common/field/InputCustom';
import InputSearchCustom from 'src/components/common/field/InputSearchCustom';
import InputTextAreaAutosize from 'src/components/common/field/InputTextAreaAutosize';
import RecordCommon from 'src/components/common/table/Record';
import InputCustomNumber from 'src/components/quotations/DialogForm/InputCustom';
import Record from 'src/components/quotations/DialogForm/Record';
import RecordProduct from 'src/components/quotations/DialogForm/RecordProduct';
import IconButtonCustom from 'src/components/quotations/IconButtonCustom';

import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { formatNumber, getField, getToday, isEmpty } from 'src/common/helper';
import { FieldTableType } from 'src/components/common/table/TypeCustom';
import { useNotify } from 'src/context/ContextNotify';
import { getAPI, postAPI } from 'src/utils/api';
import { getToken, getUserLogged } from 'src/utils/auth';
import { DataQuoteType, ItemType } from './TypeCustom';

const typographyLabel = {
  fontSize: '14px',
  lineHeight: '16.8px',
  fontWeight: 500,
  width: '100px',
  mr: '20px',
};

const colorRequired = {
  color: '#ED5565',
  fontSize: '14px',
  fontWeight: '500',
};

const inputSX = {
  width: '160px',
  height: '48px',
};

const sizeSkeletoField = {
  width: '160px',
  height: '48px',
};

const sxLableDropDown = {
  color: '#434154',
  '&.Mui-focused': {
    color: '#434154',
    transform: 'translate(14px, 16px) scale(1)',
  },
  '&.MuiInputLabel-root': {
    color: '#434154',
    transform: 'translate(14px, 16px) scale(1)',
  },
};

const StackCenter = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
});

const fieldsDefine: FieldTableType[] = [
  {
    label: '入金日',
    field: 'quotedate',
    align: 'left',
    width: '25%',
  },
  {
    label: '医師',
    field: 'doctor',
    align: 'left',
    width: '25%',
  },
  {
    label: 'タイトル',
    field: 'method',
    align: 'left',
    width: '25%',
  },
  {
    label: '総額',
    field: 'amount',
    align: 'left',
    width: '25%',
  },
];

const defaultItem = {
  surgeryid: null,
  surgeryname: '',
  qty: null,
  price: null,
  price_mornitor: null,
  price_salon: null,
  price_partial: null,
  price_full_face: null,
  roundup: false,
  type: 1,
  apply_discount: false,
};

const defaultError = {
  quotedate: true,
  quotenumber: true,
  patientname: true,
  patientid: true,
  title: true,
  doctorid: true,
  person: true,
  duration: true,
  typeid: true,
  discountid: true,
};

const defaultErrorItem = {
  index: -1,
  fieldName: '',
  type: 'items',
};

const dataQuoteDefault = {
  discount: [],
  surgeries: [],
  products: [],
};

const defaultForm = {
  caseid: 0,
  discount: 0,
  discountid: 1,
  doctorid: null,
  duration: 1,
  memo: '',
  normal_only: false,
  note: '',
  payment_note: '',
  patient_select: 1,
  display_price_type: [1],
  patientid: '',
  patientno: '',
  person: null,
  quotedate: getToday(),
  quoteid: null,
  status: 1,
  title: '',
  typeid: 1,
  quotenumber: null,
  patientname: '',
  campaigncode: '',
};

type DataFormType = {
  caseid: number;
  discount: number | null;
  discountid: number | null;
  doctorid: number | null;
  duration: number | null;
  memo: string;
  normal_only: boolean;
  note: string;
  payment_note: string;
  patient_select: number | null;
  display_price_type: number[];
  patientid: string;
  patientno: string;
  person: number | null;
  quotedate: string | null;
  quoteid: number | null;
  status: number | null;
  title: string;
  typeid: number | null;
  quotenumber: number | null;
  patientname: string;
  campaigncode: string | null;
};

interface DialogDetailProps {
  open: boolean;
  patientId: number;
  quoteId: number;
  openFormPayment?: (info: any) => void;
  onClose: (isRefreshList?: boolean) => void;
  onSave?: () => void;
  onDelete?: (data: any | undefined) => void;
}

const DialogDetail: React.FC<DialogDetailProps> = (props) => {
  const notifyContext = useNotify();
  const [dataForm, setDataForm] = useState<DataFormType>(defaultForm);
  const [items, setItems] = useState<ItemType[]>([defaultItem]);
  const [itemsProduct, setItemsProduct] = useState<ItemType[]>([]);
  const [dataSelect, setDataSelect] = useState<any>({});
  const [dataQuote, setDataQuote] = useState<DataQuoteType>(dataQuoteDefault);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState(defaultError);
  const [errorsItems, setErrorsItems] = useState(defaultErrorItem);
  const [paymentsHistory, setPaymentsHistory] = useState<any[]>([]);
  const [priceTypeSelected, setPriceTypeSelected] = React.useState<any[]>([
    { id: 1, name: '通常価格' },
  ]);

  const handleClickPayment = () => {
    const data = {
      paymentid: 0,
      patientid: props.patientId,
      caseid: 0,
      quoteid: props.quoteId,
    };

    props.openFormPayment?.(data);
  };

  const calPriceMonitor = (
    priceMonitor: number | null,
    discountid: number | null
  ) => {
    let priceCal = priceMonitor;

    if (discountid && priceMonitor) {
      const discountItem = dataQuote.discount.find(
        (item: any) => Number(item.id) === Number(discountid)
      );

      priceCal = priceMonitor * Number(getField(discountItem, 'percentage', 1));
    }

    return priceCal;
  };

  const calPriceMonitorInitEdit = (
    priceMonitor: number | null,
    discountid: number | null,
    discounts: any
  ) => {
    let priceCal = priceMonitor;

    if (discountid && priceMonitor) {
      const discountItem = discounts.find(
        (item: any) => Number(item.id) === Number(discountid)
      );

      priceCal = priceMonitor * Number(getField(discountItem, 'percentage', 1));
    }

    return priceCal;
  };

  const handleChangeDataItem = (
    index: number,
    fieldName: string,
    value: string | number | boolean | null
  ) => {
    let fieldChange = {};

    switch (fieldName) {
      case 'surgeryid': {
        const surgery = dataQuote.surgeries.find(
          (item) => item.id && item.id === Number(value)
        );

        fieldChange = {
          surgeryid: Number(value),
          price: getField(surgery, 'price', items[index].price),
          price_mornitor: getField(
            surgery,
            'price_mornitor',
            items[index].price_mornitor
          ),
          price_salon: calPriceMonitor(
            getField(surgery, 'price_mornitor', items[index].price_mornitor),
            dataForm.discountid
          ),
          price_partial: null,
          price_full_face: null,
          qty: 1,
        };
        break;
      }
      case 'apply_discount': {
        fieldChange = {
          apply_discount: Boolean(value),
          roundup: false,
        };
        break;
      }
      default: {
        fieldChange = {
          [fieldName]: value,
        };
        break;
      }
    }

    setItems((prevItems) => {
      return prevItems.map((item, idx) => {
        if (idx === index) {
          return {
            ...item,
            ...fieldChange,
          };
        }
        return item;
      });
    });
  };

  const handleChangeDataProduct = (
    index: number,
    fieldName: string,
    value: string | number | boolean | null
  ) => {
    let fieldChange = {};

    switch (fieldName) {
      case 'surgeryid': {
        const product = dataQuote.products.find(
          (item) => item.id && Number(item.id) === Number(value)
        );

        fieldChange = {
          surgeryid: Number(value),
          price: getField(product, 'price', itemsProduct[index].price),
          price_mornitor: getField(
            product,
            'price_mornitor',
            itemsProduct[index].price_mornitor
          ),
          price_salon: calPriceMonitor(
            getField(
              product,
              'price_mornitor',
              itemsProduct[index].price_mornitor
            ),
            dataForm.discountid
          ),
          price_partial: null,
          price_full_face: null,
          qty: 1,
        };
        break;
      }
      case 'apply_discount': {
        fieldChange = {
          apply_discount: Boolean(value),
          roundup: false,
        };
        break;
      }
      default: {
        fieldChange = {
          [fieldName]: value,
        };
        break;
      }
    }

    setItemsProduct((prevItems) => {
      return prevItems.map((item, idx) => {
        if (idx === index) {
          return {
            ...item,
            ...fieldChange,
          };
        }
        return item;
      });
    });
  };

  const handleChange =
    (name: string) => (value: number | string | number[] | boolean | null) => {
      let fieldChange = {};

      switch (name) {
        case 'doctorid': {
          getQuotesurgery(Number(value));
          setDataForm((prevState) => ({
            ...prevState,
            doctorid: Number(value),
          }));
          break;
        }
        case 'discountid': {
          const newItems = items.map((item) => {
            return {
              ...item,
              price_salon: calPriceMonitor(item.price_mornitor, Number(value)),
            };
          });

          const newItemsProduct = itemsProduct.map((item) => {
            return {
              ...item,
              price_salon: calPriceMonitor(item.price_mornitor, Number(value)),
            };
          });

          setItems(newItems);
          setItemsProduct(newItemsProduct);

          fieldChange = {
            discountid: value,
          };
          break;
        }
        default: {
          fieldChange = {
            [name]: value,
          };
          break;
        }
      }

      setDataForm((prevState) => ({
        ...prevState,
        ...fieldChange,
      }));
    };

  const handleChangeDate = (date: Dayjs | null) => {
    const dateFormat = date ? date.format('YYYY/MM/DD') : null;
    handleChange('quotedate')(dateFormat);
  };

  const handleChangeInputSearch = (value: any | undefined) => {
    handleChange('patientid')(getField(value, 'patientno', ''));
  };

  const handleClose = () => {
    props.onClose();
  };

  const handleClickAddRowItem = () => {
    setItems((prevState) => [...prevState, defaultItem]);
  };

  const handleDeleteRowItem = (index: number) => {
    let deleteItems = items;

    deleteItems.splice(index, 1);

    setItems((prevState) => [...deleteItems]);
  };

  const handleChooseTemplate = (template: any) => {
    let listItem: ItemType[] = [];
    template.items.map((item: any) => {
      let value = JSON.parse(JSON.stringify(defaultItem));
      value.surgeryid = getField(item.surgery, 'id', null);
      value.qty = getField(item, 'qty', 1);
      value.price = getField(item.surgery, 'price', null);
      value.price_mornitor = getField(item.surgery, 'price_mornitor', null);
      value.price_salon = getField(item.surgery, 'price', null);
      value.price_partial = getField(item.surgery, 'price_partial', null);
      value.price_full_face = getField(item.surgery, 'price_full_face', null);
      value.apply_discount = false;
      value.roundup = false;
      value.surgeryname = getField(item.surgery, 'surgery', '');
      value.type = getField(item, 'type', 1);

      listItem.push(value);
      return item;
    });
    setItems((prevState) => [...prevState, ...listItem]);
  };

  const handleClickAddRowProduct = () => {
    setItemsProduct((prevState) => [...prevState, defaultItem]);
  };

  const handleDeleteRowProduct = (index: number) => {
    let deleteItems = itemsProduct;

    deleteItems.splice(index, 1);

    setItemsProduct((prevState) => [...deleteItems]);
  };

  const setDataEdit = (data: any) => {
    const patient = getField(data, 'patient', {});
    const quote = getField(data, 'quote', {});
    const items = getField(quote, 'items', []);
    const discounts = getField(data, 'discount', []);

    const displayPriceType = getField(quote, 'display_price_type', [1])
    let newSelected = [...priceTypeSelected];
    displayPriceType.map((priceType: number) => {
      if (priceType === 2) {
        newSelected.push({
          id: Number(2),
          name: '部分モニター価格',
        });
      }
      if (priceType === 3) {
        newSelected.push({
          id: Number(3),
          name: '顔出しモニター価格',
        });
      }
      return priceType
    })
    setPriceTypeSelected(newSelected)

    setDataForm({
      caseid: getField(quote, 'caseid', null),
      discount: Number(getField(quote, 'discount', 0)) * 100,
      discountid: getField(quote, 'discountid', null),
      doctorid: getField(quote, 'doctorid', null),
      duration: getField(quote, 'duration', null),
      quoteid: getField(quote, 'id', null),
      memo: getField(quote, 'memo', ''),
      normal_only: getField(quote, 'normal_only', false),
      note: getField(quote, 'note', ''),
      payment_note: getField(quote, 'payment_note', ''),
      patient_select: getField(quote, 'patient_select', null),
      display_price_type: displayPriceType,
      patientid: getField(patient, 'id', ''),
      patientno: getField(patient, 'patientno', ''),
      person: getField(quote, 'person', null),
      quotedate: getField(quote, 'quotedate', null),
      status: getField(quote, 'status', null),
      title: getField(quote, 'title', ''),
      typeid: getField(quote, 'typeid', null),
      patientname: getField(patient, 'name', ''),
      quotenumber: getField(quote, 'quotenumber', null),
      campaigncode: getField(quote, 'campaigncode', null),
    });

    let listItem: any[] = [];
    let listItemProduct: any[] = [];

    for (let item of items) {
      if (Number(getField(item, 'status', 0)) === 1) {
        let value = JSON.parse(JSON.stringify(defaultItem));
        value.surgeryid =
          item.surgery.length !== 0
            ? getField(item.surgery, 'id', null)
            : getField(item.product, 'id', null);
        value.qty = getField(item, 'qty', null);
        value.price = getField(item, 'price', null);
        value.price_mornitor = getField(item, 'price_mornitor', null);
        value.price_salon = getField(item, 'price_salon', null);
        value.apply_discount = getField(item, 'apply_discount', false);
        value.price_partial = calPriceMonitorInitEdit(
          getField(item, 'price_mornitor', item.price_mornitor),
          data.discount[0]?.id || 1,
          discounts
        );
        value.price_full_face = calPriceMonitorInitEdit(
          getField(item, 'price_mornitor', item.price_mornitor),
          data.discount[1]?.id || 2,
          discounts
        );
        value.roundup = getField(item, 'roundup', false);
        value.surgeryname = getField(item, 'surgeryname', '');
        value.type = getField(item, 'type', 1);

        Number(getField(item, 'type', 1)) === 1
          ? listItem.push(value)
          : listItemProduct.push(value);
      }
    }

    if (listItem.length !== 0) {
      setItems((prevState) => [...listItem]);
    }

    if (listItemProduct.length !== 0) {
      setItemsProduct((prevState) => [...listItemProduct]);
    }
  };

  const convertItemType = (data: any[], id: string, name: string) => {
    const converted = data
      ? data.map((item: any) => ({
          id: String(item[id]),
          name: item[name],
        }))
      : [];

    return converted;
  };

  const getQuotesurgery = (doctorId: number) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      getAPI(
        `/patient/quote-doctor-surgery?doctorid=${doctorId}&quoteid=${props.quoteId}&token=${token}`
      )
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            setDataQuote((prevState) => ({
              ...prevState,
              surgeries: getField(res.data, 'surgeries', []),
            }));
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const getQuoteDetail = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      setIsLoading(true);
      getAPI(
        `/patient/quote?patientid=${props.patientId}&quoteid=${props.quoteId}&token=${token}`
      )
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            // Is edit
            if (props.quoteId !== 0) {
              setDataEdit(res.data);
            } else {
              const userLogin = getUserLogged();

              const patient = getField(res.data, 'patient', {});
              if (userLogin && userLogin.user && userLogin.user.id) {
                setDataForm((prevState) => ({
                  ...prevState,
                  person: userLogin.user.id,
                  patientid: patient?.id || '',
                  patientno: patient?.patientno || '',
                  patientname: patient?.name || '',
                }));
              }
            }

            const doctorsRes = getField(res.data, 'doctors', []);
            const usersRes = getField(res.data, 'users', []);
            const typesRes = getField(res.data, 'types', []);
            const discountRes = getField(res.data, 'discount', []);

            setDataQuote((prevState) => ({
              ...prevState,
              discount: getField(res.data, 'discount', []),
              surgeries: getField(res.data, 'surgeries', []),
              products: getField(res.data, 'products', []),
            }));

            setDataSelect({
              doctors: convertItemType(doctorsRes, 'id', 'name'),
              users: convertItemType(usersRes, 'userid', 'name'),
              types: convertItemType(typesRes, 'id', 'name'),
              discount: convertItemType(discountRes, 'id', 'discount'),
            });

            setIsLoading(false);
          }
          if (res.code === 403 || res.code === 500 || res.code === 501) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg,
              type: 'error',
            });
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const formatPrice = (payments: any[]) => {
    const paymentFormat = payments.map((item: any) => {
      return {
        ...item,
        amount: formatNumber(getField(item, 'amount', 0)) + '円',
      };
    });

    return paymentFormat;
  };

  const getQuoteView = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      setIsLoading(true);
      getAPI(`/patient/view?patientid=${props.patientId}&token=${token}`)
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            const payments = formatPrice(getField(res.data, 'payments', []));
            setPaymentsHistory(payments);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const getPriceSalon = (item: ItemType) => {
    if (dataForm.patient_select === 1) {
      return item.price;
    }
    if (dataForm.patient_select === 2) {
      return item.price_partial;
    }
    return item.price_full_face;
  };

  const filterItem = () => {
    const newItem = items
      .filter((item) => item.surgeryid !== null)
      .map((newIte) => {
        return {
          ...newIte,
          price_salon:
            props.quoteId !== 0 ? getPriceSalon(newIte) : newIte.price_salon,
        };
      });

    const newitemsProduct = itemsProduct
      .filter((item) => item.surgeryid !== null)
      .map((itemPro) => {
        return {
          ...itemPro,
          price_salon:
            props.quoteId !== 0 ? getPriceSalon(itemPro) : itemPro.price_salon,
          type: 2,
        };
      });

    return [...newItem, ...newitemsProduct];
  };

  const validate = () => {
    if (isEmpty(dataForm.quotedate)) {
      setErrors({ ...defaultError, quotedate: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '見積日を選択してください',
        type: 'error',
      });
      return false;
    }

    if (isEmpty(dataForm.patientid)) {
      setErrors({ ...defaultError, patientid: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '患者IDを入力してください',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(dataForm.title)) {
      setErrors({ ...defaultError, title: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: 'タイトルを入力してください',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(dataForm.doctorid)) {
      setErrors({ ...defaultError, doctorid: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '医師を選択してください',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(dataForm.person)) {
      setErrors({ ...defaultError, person: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '担当者をお選びください',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(dataForm.typeid)) {
      setErrors({ ...defaultError, typeid: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: 'タイプを選択してください',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(dataForm.discountid)) {
      setErrors({ ...defaultError, discountid: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '価格タイプを選択してください',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(dataForm.duration)) {
      setErrors({ ...defaultError, duration: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '期間を入力してください',
        type: 'error',
      });
      return false;
    }

    setErrors(defaultError);

    notifyContext.setNotifyMessage({
      show: false,
      message: '',
      type: 'error',
    });

    return true;
  };

  const validateItems = (type: 'items' | 'products' = 'items') => {
    let checkError = true;

    let data = type === 'items' ? items : itemsProduct;

    data.forEach((item, index) => {
      if (item.surgeryid !== null) {
        if (isEmpty(item.qty)) {
          setErrorsItems({ index, fieldName: 'qty', type });
          notifyContext.setNotifyMessage({
            show: true,
            message: '数量を入力してください',
            type: 'error',
          });
          checkError = false;
        }

        if (isEmpty(item.price)) {
          setErrorsItems({ index, fieldName: 'price', type });
          notifyContext.setNotifyMessage({
            show: true,
            message: '価格を入力してください',
            type: 'error',
          });
          checkError = false;
        }
      }
    });

    if (checkError) {
      setErrorsItems(defaultErrorItem);

      notifyContext.setNotifyMessage({
        show: false,
        message: '',
        type: 'error',
      });
    }

    return checkError;
  };

  const handleSave = () => {
    if (!validate() || !validateItems() || !validateItems('products')) {
      return;
    }
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      if (filterItem().length === 0) {
        notifyContext.setNotifyMessage({
          show: true,
          message: '詳細は必須です',
          type: 'error',
        });
        return;
      }

      const data = {
        ...dataForm,
        items: filterItem(),
      };

      postAPI(`/patient/save-quote?&token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
            notifyContext.setNotifyMessage({
              show: true,
              message: '保存出来ました。',
              type: 'success',
            });
            props.onClose(true);
          }
          if (res.code === 501 || res.code === 500) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg,
              type: 'error',
            });
          }
        })
        .finally(() => {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const handleDelete = () => {
    props.onDelete?.({ id: props.quoteId });
  };

  useEffect(() => {
    if (props.open) {
      getQuoteDetail();
      getQuoteView();
    } else {
      setPaymentsHistory([]);
      setItems([defaultItem]);
      setItemsProduct([]);
      setDataForm(defaultForm);
      setErrors(defaultError);
    }
    // eslint-disable-next-line
  }, [props.open]);

  const handleChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value, name } = event.target;
    let newSelected = [...priceTypeSelected];
    if (checked) {
      newSelected.push({
        id: Number(value),
        name: name,
      });
    } else {
      newSelected = newSelected.filter((item) => item.name !== name);
    }
    setPriceTypeSelected(newSelected);
    const priceType = newSelected.map((item) => item.id);
    setDataForm((prevState) => ({
      ...prevState,
      display_price_type: priceType,
    }));
  };

  const handleChangeDropDown = (event: SelectChangeEvent<string | number>) => {
    const patientSelect = event.target.value ? Number(event.target.value) : 1;
    setDataForm((prevState) => ({
      ...prevState,
      patient_select: Number(patientSelect),
    }));
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '1020px',
          m: '0px',
        },
      }}
    >
      <DialogTitle id='scroll-dialog-title' sx={{ p: '24px', pb: '0px' }}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography
            sx={{ fontSize: '20px', lineHeight: '24px', fontWeight: 500 }}
          >
            新規見積
          </Typography>
          <IconButton
            aria-label='toggle password visibility'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close-blue.svg'
              alt='Close'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: '0px 10px 24px 24px',
          '& .simplebar-track.simplebar-vertical .simplebar-scrollbar:before': {
            backgroundColor: '#CCD1D9',
          },
          overflow: 'hidden',
        }}
      >
        <SimpleBar
          style={{ height: 'calc(100vh - 160px)', borderWidth: '18px' }}
        >
          <Stack spacing='10px' sx={{ pr: '14px' }}>
            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                予約日
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>

              {!isLoading ? (
                <DatePickerCustom
                  value={dayjs(dataForm.quotedate)}
                  onChange={handleChangeDate}
                  error={!errors.quotedate}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <StackCenter>
              <Stack>
                <Typography sx={{ ...typographyLabel }}>
                  患者情報
                  <Typography component='label' {...colorRequired}>
                    *
                  </Typography>
                </Typography>
                <Typography
                  sx={{ ...typographyLabel, fontSize: '12px', fontWeight: 400 }}
                >
                  (どちらか)
                </Typography>
              </Stack>
              {!isLoading ? (
                <StackCenter>
                  <InputSearchCustom
                    labelInput='患者名'
                    name='patientname'
                    value={dataForm.patientname}
                    onChange={handleChangeInputSearch}
                    sx={{ mr: '10px' }}
                    readonly={true}
                    error={!errors.patientname}
                  />
                  <InputSearchCustom
                    labelInput='患者ID'
                    name='patientid'
                    value={dataForm.patientno}
                    readonly={true}
                    error={!errors.patientid}
                  />
                </StackCenter>
              ) : (
                <StackCenter>
                  <Skeleton
                    variant='rounded'
                    sx={{ ...sizeSkeletoField, mr: '10px' }}
                  />
                  <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
                </StackCenter>
              )}
            </StackCenter>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                タイトル
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>

              {!isLoading ? (
                <InputCustom
                  value={dataForm.title}
                  type='text'
                  labelInput='入力してください'
                  name='title'
                  onChange={handleChange('title')}
                  sx={{ ml: '12px', width: '100%' }}
                  error={!errors.title}
                />
              ) : (
                <Skeleton
                  variant='rounded'
                  sx={{ ...sizeSkeletoField, width: '100%', ml: '10px' }}
                />
              )}
            </StackCenter>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                医師
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              {!isLoading ? (
                <DropDown
                  selected={dataForm.doctorid}
                  idSelect='doctor-select'
                  placeholder='選択してください'
                  items={getField(dataSelect, 'doctors', [])}
                  onChange={handleChange('doctorid')}
                  sxInput={{ ...inputSX }}
                  sxLabel={sxLableDropDown}
                  error={!errors.doctorid}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                担当者
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              {!isLoading ? (
                <DropDown
                  selected={dataForm.person}
                  idSelect='person-select'
                  placeholder='選択してください'
                  items={getField(dataSelect, 'users', [])}
                  onChange={handleChange('person')}
                  sxInput={{ ...inputSX }}
                  sxLabel={sxLableDropDown}
                  error={!errors.person}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                見積タイプ
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              {!isLoading ? (
                <DropDown
                  selected={dataForm.typeid}
                  idSelect='typeid-select'
                  // placeholder='選択してください'
                  items={getField(dataSelect, 'types', [])}
                  onChange={handleChange('typeid')}
                  sxInput={{ ...inputSX }}
                  sxLabel={sxLableDropDown}
                  error={!errors.typeid}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            {props.quoteId !== 0 && (
              <StackCenter>
                <Typography sx={{ ...typographyLabel }}>価格タイプ</Typography>
                {!isLoading ? (
                  <FormGroup>
                    <FormControlLabel
                      disabled
                      control={<Checkbox defaultChecked />}
                      label='通常価格'
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          onChange={(event) => handleChangeCheckbox(event)}
                          value={2}
                          name={'部分モニター価格'}
                          checked={dataForm.display_price_type.includes(2)}
                        />
                      }
                      label='部分モニター価格'
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          onChange={(event) => handleChangeCheckbox(event)}
                          value={3}
                          name={'顔出しモニター価格'}
                          checked={dataForm.display_price_type.includes(3)}
                        />
                      }
                      label='顔出しモニター価格'
                    />
                  </FormGroup>
                ) : (
                  <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
                )}
              </StackCenter>
            )}

            {props.quoteId !== 0 && (
              <StackCenter>
                <Typography sx={{ ...typographyLabel }}>
                  患者選択
                  <Typography component='label' {...colorRequired}>
                    *
                  </Typography>
                </Typography>
                {!isLoading ? (
                  <Select
                    labelId='demo-multiple-name-label'
                    id='demo-multiple-name'
                    value={getField(dataForm, 'patient_select', 1)}
                    onChange={handleChangeDropDown}
                    sx={{ ...inputSX }}
                  >
                    {priceTypeSelected.map((option, index) => (
                      <MenuItem key={index} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
                )}
              </StackCenter>
            )}

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>割引%</Typography>

              {!isLoading ? (
                <InputCustom
                  value={dataForm.discount}
                  type='number'
                  name='discount'
                  labelInput='0%'
                  onChange={handleChange('discount')}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            {/* future function */}
            {/* <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                キャンペーンコード%
              </Typography>

              {!isLoading ? (
                <InputCustom
                  value={dataForm.campaigncode}
                  type='text'
                  name='campaigncode'
                  labelInput='入力してください'
                  onChange={handleChange('campaigncode')}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter> */}

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                最大施術時間
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>

              {!isLoading ? (
                <InputCustomNumber
                  value={dataForm.duration}
                  type='number'
                  name='duration'
                  onChange={handleChange('duration')}
                  error={!errors.duration}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <Box sx={{ m: '15px 0px 5px !important' }}>
              <Divider />
            </Box>

            <Box sx={{ width: '100%' }}>
              <Record
                items={items}
                dataQuote={dataQuote}
                loading={isLoading}
                discount={dataForm.discount}
                discountid={dataForm.discountid}
                displayPriceType={dataForm.display_price_type}
                onChangeItem={handleChangeDataItem}
                onClickAddRow={handleClickAddRowItem}
                onClickDelete={handleDeleteRowItem}
                handleChooseTemplate={handleChooseTemplate}
                error={errorsItems}
              />
            </Box>

            <Box sx={{ m: '15px 0px 5px !important' }}>
              <Divider />
            </Box>

            <Box sx={{ width: '100%' }}>
              <RecordProduct
                items={itemsProduct}
                dataQuote={dataQuote}
                loading={isLoading}
                discount={dataForm.discount}
                discountid={dataForm.discountid}
                displayPriceType={dataForm.display_price_type}
                onChangeItem={handleChangeDataProduct}
                onClickAddRow={handleClickAddRowProduct}
                onClickDelete={handleDeleteRowProduct}
                error={errorsItems}
              />
            </Box>

            <Box sx={{ m: '15px 0px 5px !important' }}>
              <Divider />
            </Box>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>備考</Typography>

              <InputTextAreaAutosize
                value={dataForm.note}
                onChange={handleChange('note')}
                width='100%'
                height='104px'
                m='0px 0px 0px 10px'
              />
            </StackCenter>

            <Box sx={{ m: '15px 0px 10px !important' }}>
              <Divider />
            </Box>

            <Box>
              <Typography sx={{ ...typographyLabel, fontSize: '20px' }}>入金予定</Typography>
              <InputTextAreaAutosize
                value={dataForm.payment_note}
                onChange={handleChange('payment_note')}
                width='100%'
                height='104px'
                m='10px 0'
              />
            </Box>

            <Box width={'100%'}>
              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                width={'100%'}
              >
                <Box>
                  {props.quoteId !== 0 && props.onDelete && (
                    <BtnOutlineGradient
                      text='削除'
                      handleButtonClick={handleDelete}
                      disabled={isLoading}
                      sxText={{
                        background: '#DA4453',
                      }}
                      sx={{
                        width: '80px',
                        background: '#DA4453',
                      }}
                      sxButton={{
                        minWidth: '56px',
                      }}
                    />
                  )}
                </Box>
                <Stack direction={'row'} spacing={'8px'}>
                  <BtnOutlineGradient
                    text='キャンセル'
                    handleButtonClick={handleClose}
                    disabled={isLoading}
                  />
                  <BtnGradient
                    text='登録'
                    handleButtonClick={handleSave}
                    disabled={isLoading}
                  />
                </Stack>
              </Stack>

              <Box sx={{ m: '20px 0px !important' }}>
                <Divider />
              </Box>

              <Box>
                <Typography sx={{ ...typographyLabel, fontSize: '20px' }}>入金履歴</Typography>

                <Box width={'638px'} mt={'10px'}>
                  {isLoading ? (
                    <Skeleton
                      variant='rounded'
                      sx={{ height: '170px', width: '100%' }}
                    />
                  ) : (
                    <RecordCommon
                      items={paymentsHistory}
                      fields={fieldsDefine}
                      sx={{ maxHeight: '170px' }}
                    />
                  )}

                  <IconButtonCustom
                    text='追加する'
                    src='/images/ic-plus-blue.svg'
                    tooltip='Add payment'
                    onClick={handleClickPayment}
                  />
                </Box>
              </Box>
            </Box>
          </Stack>
        </SimpleBar>
      </DialogContent>
    </Dialog>
  );
};

export default DialogDetail;
